import { useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { CalendarDays } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { Section } from '../../components/Section'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ThumbStack } from '../../components/profile/ThumbStack'
import { unknownMemberName } from '../../lib/commonStrings'
import { formatEventDateRange } from '../../lib/formatDate'
import { placeholders } from '../../lib/skeletonUtils'
import type { Event, GroupEventRequest, UserProfile } from '../../types'

export interface VisibleRequest {
  request: GroupEventRequest
  event: Event
}

/**
 * Events members suggested to a group that requires link approval. Admins
 * approve or decline; everyone else sees what's waiting.
 */
export function PendingRequestsSection({
  loading,
  requests,
  profiles,
  isAdmin,
  onApprove,
  onReject,
}: {
  loading?: boolean
  requests: VisibleRequest[]
  profiles: Record<string, UserProfile>
  isAdmin: boolean
  onApprove: (request: GroupEventRequest) => Promise<void>
  onReject: (request: GroupEventRequest) => Promise<void>
}) {
  const { locale } = useLocaleContext()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [declining, setDeclining] = useState<VisibleRequest | null>(null)

  if (loading) {
    return (
      <Section loading>
        {placeholders(2).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
      </Section>
    )
  }
  if (requests.length === 0) return null

  async function run(id: string, action: () => Promise<void>) {
    setBusyId(id)
    try {
      await action()
    } catch {
      // runMutation already toasted.
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Section title={fbt('Suggested events', 'Section heading for events members suggested to a group that an admin has to approve')} testId="group-suggested-events">
      <ul className="flex flex-col gap-1">
        {requests.map(({ request, event }) => {
          const name = profiles[request.requestedBy]?.displayName?.trim() || unknownMemberName()
          const busy = busyId === request.id
          return (
            <li key={request.id} className="flex items-center gap-3 rounded-lg py-2">
              <ThumbStack srcs={[event.imageUrl]} icon={CalendarDays} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{event.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {formatEventDateRange(event.startDate, event.endDate, locale)} ·{' '}
                  <fbt desc="Who suggested an event to the group, under the event in the suggested events list">
                    Suggested by <fbt:param name="name">{name}</fbt:param>
                  </fbt>
                </p>
              </div>
              {isAdmin && (
                <div className="flex shrink-0 gap-1.5">
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => setDeclining({ request, event })}>
                    {fbt('Decline', 'Button that declines an event suggested to the group')}
                  </Button>
                  <Button size="sm" disabled={busy} onClick={() => void run(request.id, () => onApprove(request))}>
                    {fbt('Approve', 'Button that approves an event suggested to the group and links it')}
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>

      <ConfirmDialog
        open={!!declining}
        onOpenChange={open => !open && setDeclining(null)}
        title={fbt(
          'Decline ' + fbt.param('event', declining?.event.name ?? '') + '?',
          'Title of the dialog confirming an admin declines an event suggested to the group',
        )}
        description={fbt(
          'It won’t be linked to the group. Members can still suggest it again later.',
          'Description of the dialog confirming an admin declines an event suggested to the group',
        )}
        confirmLabel={fbt('Decline', 'Confirm button that declines an event suggested to the group')}
        destructive
        onConfirm={() => {
          if (declining) void run(declining.request.id, () => onReject(declining.request))
          setDeclining(null)
        }}
      />
    </Section>
  )
}
